import sodium from "libsodium-wrappers-sumo";

/** Domain separation prefixes to prevent cross-protocol signature confusion. */
export const DOMAIN_DID_DOCUMENT = "DID-DOCUMENT:";
export const DOMAIN_TRANSFER_INTENT = "TRANSFER-INTENT:";
export const DOMAIN_TRANSFER_ACCEPT = "TRANSFER-ACCEPT:";

/**
 * JCS (JSON Canonicalization Scheme — RFC 8785) serialization.
 * Produces deterministic JSON bytes: sorted keys at every level, UTF-8 encoded.
 *
 * Note: Uses sodium.from_string() instead of TextEncoder for React Native compatibility.
 * Callers must ensure sodium.ready has been awaited before calling this function.
 */
export function canonicalize(obj: unknown): Uint8Array {
  const json = jsonCanonical(obj) ?? "";
  return sodium.from_string(json);
}

function jsonCanonical(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  if (value === null) return "null";
  if (typeof value === "boolean" || typeof value === "number") return JSON.stringify(value);
  if (typeof value === "string") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return "[" + value.map(jsonCanonical).join(",") + "]";
  }
  if (typeof value === "object") {
    const keys = Object.keys(value as Record<string, unknown>).sort();
    const entries = keys
      .map((k) => {
        const v = jsonCanonical((value as Record<string, unknown>)[k]);
        if (v === undefined) return undefined;
        return JSON.stringify(k) + ":" + v;
      })
      .filter((entry): entry is string => entry !== undefined);
    return "{" + entries.join(",") + "}";
  }
  return JSON.stringify(value);
}

// --- Base58btc encoding (multibase z-prefix) ---

const BASE58_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

function base58Encode(bytes: Uint8Array): string {
  let zeros = 0;
  for (const b of bytes) {
    if (b === 0) zeros++;
    else break;
  }

  let num = BigInt(0);
  for (const b of bytes) {
    num = num * 256n + BigInt(b);
  }

  let result = "";
  while (num > 0n) {
    const remainder = Number(num % 58n);
    num = num / 58n;
    result = BASE58_ALPHABET[remainder] + result;
  }

  return "1".repeat(zeros) + result;
}

function base58Decode(str: string): Uint8Array {
  let zeros = 0;
  for (const c of str) {
    if (c === "1") zeros++;
    else break;
  }

  let num = BigInt(0);
  for (const c of str.slice(zeros)) {
    const idx = BASE58_ALPHABET.indexOf(c);
    if (idx === -1) throw new Error(`Invalid base58 character: ${c}`);
    num = num * 58n + BigInt(idx);
  }

  const hex = num === 0n ? "" : num.toString(16).padStart(2, "0");
  const paddedHex = hex.length % 2 ? "0" + hex : hex;
  const dataBytes = new Uint8Array(
    (paddedHex.match(/.{2}/g) ?? []).map((b) => parseInt(b, 16)),
  );

  const result = new Uint8Array(zeros + dataBytes.length);
  result.set(dataBytes, zeros);
  return result;
}

/**
 * Encode an Ed25519 public key as multibase z-prefixed base58btc.
 * Prepends the multicodec Ed25519 prefix (0xed, 0x01) per did:key spec.
 */
export function publicKeyToMultibase(publicKey: Uint8Array): string {
  const prefixed = new Uint8Array(2 + publicKey.length);
  prefixed[0] = 0xed;
  prefixed[1] = 0x01;
  prefixed.set(publicKey, 2);
  return "z" + base58Encode(prefixed);
}

/**
 * Decode a multibase z-prefixed base58btc string back to raw Ed25519 public key bytes.
 * Strips the multicodec prefix.
 */
export function multibaseToPublicKey(multibase: string): Uint8Array {
  if (!multibase.startsWith("z")) {
    throw new Error("Multibase string must start with 'z' (base58btc)");
  }
  const decoded = base58Decode(multibase.slice(1));
  if (decoded.length < 2 || decoded[0] !== 0xed || decoded[1] !== 0x01) {
    throw new Error("Invalid Ed25519 multicodec prefix");
  }
  return decoded.slice(2);
}

/**
 * Sign a document (any JSON-serializable object) with an Ed25519 private key.
 * Returns a detached signature over the domain-separated, JCS-canonicalized bytes.
 *
 * The signature is computed over `DOMAIN_DID_DOCUMENT + canonicalize(document)`
 * to prevent cross-protocol signature confusion.
 */
export async function signDocument(
  document: object,
  privateKey: Uint8Array,
): Promise<Uint8Array> {
  await sodium.ready;
  const canonical = canonicalize(document);
  const domainPrefix = sodium.from_string(DOMAIN_DID_DOCUMENT);
  const message = new Uint8Array(domainPrefix.length + canonical.length);
  message.set(domainPrefix);
  message.set(canonical, domainPrefix.length);
  return sodium.crypto_sign_detached(message, privateKey);
}

/**
 * Verify a detached Ed25519 signature over a domain-separated, JCS-canonicalized document.
 *
 * Verifies against `DOMAIN_DID_DOCUMENT + canonicalize(document)`.
 */
export async function verifyDocumentSignature(
  document: object,
  signature: Uint8Array,
  publicKey: Uint8Array,
): Promise<boolean> {
  await sodium.ready;
  const canonical = canonicalize(document);
  const domainPrefix = sodium.from_string(DOMAIN_DID_DOCUMENT);
  const message = new Uint8Array(domainPrefix.length + canonical.length);
  message.set(domainPrefix);
  message.set(canonical, domainPrefix.length);
  try {
    return sodium.crypto_sign_verify_detached(signature, message, publicKey);
  } catch {
    return false;
  }
}

/** Parameters for building a DID document. */
export interface BuildDidDocumentParams {
  hubAddress: string;
  ownerPublicKey: Uint8Array;
  agentPublicKey: Uint8Array;
  serviceEndpoint: string;
  profileEndpoint: string;
  /** ISO 8601 timestamp for created/updated fields. Defaults to current time. */
  created?: string;
}

/** A W3C DID Document verification method. */
export interface DidVerificationMethod {
  id: string;
  type: string;
  controller: string;
  publicKeyMultibase: string;
}

/** A W3C DID Document service entry. */
export interface DidService {
  id: string;
  type: string;
  serviceEndpoint: string;
}

/** A W3C DID Document for an AgentVault hub identity. */
export interface DidDocument {
  "@context": string[];
  id: string;
  controller: string;
  verificationMethod: DidVerificationMethod[];
  authentication: string[];
  assertionMethod: string[];
  service: DidService[];
  created: string;
  updated: string;
}

/**
 * Build a W3C DID Document for an AgentVault hub identity.
 */
export function buildDidDocument(params: BuildDidDocumentParams): DidDocument {
  const { hubAddress, ownerPublicKey, agentPublicKey, serviceEndpoint, profileEndpoint } = params;
  const didId = `did:hub:${hubAddress}`;
  const ownerMultibase = publicKeyToMultibase(ownerPublicKey);
  const agentMultibase = publicKeyToMultibase(agentPublicKey);
  const controllerDid = `did:key:${ownerMultibase}`;

  const now = params.created ?? new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

  return {
    "@context": [
      "https://www.w3.org/ns/did/v1",
      "https://w3id.org/security/suites/ed25519-2020/v1",
    ],
    id: didId,
    controller: controllerDid,
    verificationMethod: [
      {
        id: `${didId}#owner-key`,
        type: "Ed25519VerificationKey2020",
        controller: didId,
        publicKeyMultibase: ownerMultibase,
      },
      {
        id: `${didId}#agent-key`,
        type: "Ed25519VerificationKey2020",
        controller: didId,
        publicKeyMultibase: agentMultibase,
      },
    ],
    authentication: [`${didId}#owner-key`],
    assertionMethod: [`${didId}#owner-key`, `${didId}#agent-key`],
    service: [
      {
        id: `${didId}#messaging`,
        type: "AgentVaultSecureChannel",
        serviceEndpoint,
      },
      {
        id: `${didId}#profile`,
        type: "AgentVaultProfile",
        serviceEndpoint: profileEndpoint,
      },
    ],
    created: now,
    updated: now,
  };
}
